import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Form, Button, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";
import {PasswordField} from "services/passViewer";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{12,128}$/;

function checkPassword(pw: string) {
  return {
    length: pw.length >= 12 && pw.length <= 128,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^\w\s]/.test(pw),
    valid: PASSWORD_REGEX.test(pw),
  };
}

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const touched = useRef({ pw: false, confirm: false });

    const rules = checkPassword(newPassword);
    const match = !!newPassword && newPassword === confirmPassword;

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return setError("Invalid or missing token.");
        if (newPassword !== confirmPassword) return setError("Passwords do not match.");

        setIsSubmitting(true);
        setError(null);
        setMessage(null);

        try {
        await axios.post(`${import.meta.env.VITE_API_URL}/auth/reset-password`, {
            token,
            new_password: newPassword,
        });

        setMessage("Password reset successful. You can now log in.");
        // Optional: Redirect to login page
        setTimeout(() => navigate("/auth/sign-in"), 2000);
        } catch (err: any) {
        setError(err?.response?.data?.detail || "Failed to reset password.");
        } finally {
        setIsSubmitting(false);
        }
    };

    return (
        <Row className="align-items-center justify-content-center g-0 min-vh-100">
        <Col xxl={4} lg={6} md={8} xs={12} className="py-8 py-xl-0">
            <Card className="smooth-shadow-md p-4">
            <h4 className="mb-3">Reset Password</h4>
            <Form onSubmit={handleReset}>
                {/* Password */}
                <PasswordField
                    id="password"
                    label="Password"
                    name="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onBlur={() => (touched.current.pw = true)}     // 👈 mark touched
                    required
                    isInvalid={touched.current.pw && !rules.valid}
                    feedbackInvalid="Please meet all password requirements."
                />
                <Form.Text className="text-muted">
                    Must be 12+ chars, include upper, lower, number, and symbol.
                </Form.Text>
                <ul className="small mt-2 mb-3">
                    <li className={rules.length ? "text-success" : "text-danger"}>12–128 characters</li>
                    <li className={rules.upper ? "text-success" : "text-danger"}>At least one uppercase letter</li>
                    <li className={rules.lower ? "text-success" : "text-danger"}>At least one lowercase letter</li>
                    <li className={rules.digit ? "text-success" : "text-danger"}>At least one number</li>
                    <li className={rules.symbol ? "text-success" : "text-danger"}>At least one symbol</li>
                </ul>

                {/* Confirm Password */}
                <PasswordField
                    id="confirmPassword"
                    label="Confirm Password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => (touched.current.confirm = true)} // 👈 mark touched
                    required
                    isInvalid={touched.current.confirm && !!confirmPassword && !match}
                    feedbackInvalid="Passwords do not match."
                />

                <Button
                type="submit"
                disabled={isSubmitting || !rules.valid || !match}
                className="w-100"
                >
                {isSubmitting ? "Resetting..." : "Reset Password"}
                </Button>

                {message && <Alert variant="success" className="mt-3">{message}</Alert>}
                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            </Form>
            </Card>
        </Col>
        </Row>
    );
};

export default ResetPassword;
